import { useState } from "react";
import Select from "react-select";
import ClarityVerificationRequest from "../Project/ClarityVerificationRequest";
import FossIdVerificationRequest from "../Project/FossIdVerificationRequest";
import SbomVerificationRequest from "../Project/SbomVerificationRequest";
import ClarityAirVerificationRequest from "../Project/ClarityAirVerificationRequest";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";

const VerificationRequestButton = ({
  onSubmit,
  buttonText = "Add verification request",
  scanToolList = [],
  "data-testid": dataTestId = "verification-request-button",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVerificationType, setSelectedVerificationType] = useState(null);
  const [activeVerificationType, setActiveVerificationType] = useState(null);
  const [hasErrors, setHasErrors] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [repoInfo, setRepoInfo] = useState({});
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundImage: "linear-gradient(to right, #1EC6FF, #367FFF)",
      border: "none",
      borderRadius: "8px",
      outline: "none",
      boxShadow: "none",
      minHeight: "40px",
      cursor: "pointer",
      "&:hover": {
        border: "none",
        outline: "none",
        boxShadow: "none",
      },
      "&:focus": {
        border: "none",
        outline: "none",
        boxShadow: "none",
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#000000",
      margin: 0,
      padding: 0,
      fontSize: "14px",
      fontWeight: "500"
    }),
    singleValue: (base) => ({
      ...base,
      color: "#000000",
      fontWeight: "500",
      fontSize: "14px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: colors.bgPrimaryColor,
      color: state.isSelected || state.isFocused ? colors.textActiveColor : colors.textPrimaryColor,
      border: state.isSelected || state.isFocused ? `1px solid ${colors.dropdownBorderColor}` : "1px solid transparent",
      boxShadow: state.isSelected || state.isFocused ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
      borderRadius: "0.25rem",
      padding: "12px 16px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      marginBottom: "3px",
      position: "relative",
      zIndex: state.isSelected || state.isFocused ? 1000 : 0,
      transition: "all 0.2s ease",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "&:hover": {
        backgroundColor: colors.bgPrimaryColor,
        color: colors.textActiveColor
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: colors.bgPrimaryColor,
      borderRadius: "0.25rem",
      marginTop: 4,
      borderTop: "none",
      boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)",
      padding: "0.125rem",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 50,
    }),
    menuList: (base) => ({
      ...base,
      borderRadius: "0.5rem",
      padding: "0.5rem",
      margin: 0,
      backgroundColor: colors.bgPrimaryColor,
      color: colors.textPrimaryColor,
    }),
    indicatorSeparator: () => ({
      display: "none"
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#000000",
      "&:hover": {
        color: "#000000"
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 12px"
    })
  };

  const handleVerificationTypeSelect = (selectedOption) => {
    if (selectedOption) {
      setActiveVerificationType(selectedOption);
      setIsModalOpen(true);
      // Keep showing the placeholder instead of the selected option
      setSelectedVerificationType(null);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedVerificationType(null);
    setActiveVerificationType(null);
  };

  const handleSubmit = (data) => {
    if (onSubmit) {
      const dataWithType = {
        ...data,
        verificationType: activeVerificationType?.value
      };
      onSubmit(dataWithType);
    }
  };

  // Dynamic options based on scanToolList with translation support
  // This approach ensures consistent translations while maintaining flexibility
  const options = scanToolList?.map(tool => {
    const toolValue = tool.value?.toLowerCase();
    
    // Map tool values to translation keys for consistent labeling
    // This ensures the same tool always shows the same translated text
    const translationMap = {
      'clarity': t("navbar.clarity-verification"),
      'fossid': t("navbar.fossid-verification"), 
      'sbom': t("navbar.sbom-verification"),
      'clarityair': t("navbar.clarity-air-verification"),
    };
    
    return {
      label: translationMap[toolValue] || tool.label, // Use translation if available, fallback to tool.label
      value: toolValue,
    };
  }) || [];

  // Don't render the component if scanToolList is empty
  if (!scanToolList || scanToolList.length === 0) {
    return null;
  }

  return (
    <>
      <div data-testid={dataTestId}>
      <Select
        options={options}
        value={selectedVerificationType}
        onChange={handleVerificationTypeSelect}
        placeholder={buttonText}
        isSearchable={false}
        styles={customSelectStyles}
        isClearable={false}
        menuPortalTarget={menuPortalTarget || undefined}
        menuPosition={menuPortalTarget ? "fixed" : "absolute"}
        className="verification-request-select"
      />
      </div>

      {/* Dynamic modal rendering based on selected verification type */}
      {activeVerificationType?.value === "clarity" && (
        <ClarityVerificationRequest
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleSubmit}
          setHasErrors={setHasErrors}
          setRequestData={setRequestData}
          setRepoInfo={setRepoInfo}
        />
      )}
      {activeVerificationType?.value === "fossid" && (
        <FossIdVerificationRequest
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleSubmit}
          setHasErrors={setHasErrors}
          setRequestData={setRequestData}
          setRepoInfo={setRepoInfo}
        />
      )}
      {activeVerificationType?.value === "sbom" && (
        <SbomVerificationRequest
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleSubmit}
        />
      )}
       {activeVerificationType?.value === "clarityair" && (
        <ClarityAirVerificationRequest
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleSubmit}
          setHasErrors={setHasErrors}
          setRequestData={setRequestData}
          setRepoInfo={setRepoInfo}
        />
      )}
    </>
  );
};

export default VerificationRequestButton;
